// src/components/modals/ProfileModals/BillingHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Spinner, Alert } from 'react-bootstrap';
import { fetchPaymentHistory, formatDateTime } from '../../../services/profile.service';
import useModalHistory from '../../../hooks/useModalHistory';

const BillingHistoryModal = ({ show, onHide, token }) => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useModalHistory(show, onHide, 'billing-history-modal');
  
  useEffect(() => {
    if (show) {
      loadPaymentHistory();
    }
  }, [show]);

  const loadPaymentHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchPaymentHistory(token);
      
      // Handle the API response structure: { status, message, data: [...] }
      let historyArray = [];
      
      if (response && response.data && Array.isArray(response.data)) {
        historyArray = response.data;
      } else if (Array.isArray(response)) {
        // Fallback if the service returns array directly
        historyArray = response;
      }
      
      setPaymentHistory(historyArray);
    } catch (error) {
      console.error('Error loading payment history:', error);
      setError('Failed to load payment history. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get status badge
  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    
    if (statusLower === 'success' || statusLower === 'completed' || statusLower === 'paid') {
      return <span className="badge bg-success">Paid</span>;
    } else if (statusLower === 'pending' || statusLower === 'processing') {
      return <span className="badge bg-warning text-dark">Pending</span>;
    } else if (statusLower === 'failed' || statusLower === 'declined') {
      return <span className="badge bg-danger">Failed</span>;
    } else if (statusLower === 'refunded') {
      return <span className="badge bg-info">Refunded</span>;
    } else {
      return <span className="badge bg-secondary">{status || 'Unknown'}</span>;
    }
  };
  
  // Format currency amount
  const formatAmount = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '-';
    
    // Convert string to number if needed
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Use browser's built-in formatter if available
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(numAmount);
    } catch (e) {
      // Fallback formatter
      return `${currency} ${numAmount.toFixed(2)}`;
    }
  };

  // Format date - handle the API's date format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="billing-history-modal-dialog"
      contentClassName="billing-history-modal-content"
      backdrop="static"
      scrollable={false}
    >
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>Billing History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}
        
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-white">Loading payment history...</p>
          </div>
        ) : paymentHistory.length === 0 ? (
          <div className="text-center py-4">
            <i className="fas fa-receipt fa-3x text-white mb-3"></i>
            <h5 className='text-white'>No Payment Records Found</h5>
            <p className="text-white">
              We couldn't find any payment records associated with your account.
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="billing-history-table" style={{ minWidth: '700px' }}>
              <thead>
                <tr>
                  <th style={{ minWidth: '100px' }}>Date</th>
                  <th style={{ minWidth: '150px' }}>Description</th>
                  <th style={{ minWidth: '100px' }}>Amount</th>
                  <th style={{ minWidth: '80px' }}>Status</th>
                  <th style={{ minWidth: '100px' }}>Method</th>
                  <th style={{ minWidth: '200px' }}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment, index) => (
                  <tr key={payment.PaymentID || index}>
                    <td>{formatDate(payment.PaymentDate)}</td>
                    <td>{payment.SubscriptionType ? `${payment.SubscriptionType} Subscription` : 'Subscription Payment'}</td>
                    <td>{formatAmount(payment.PaymentAmount, payment.Currency)}</td>
                    <td>{getStatusBadge(payment.PaymentStatus)}</td>
                    <td>
                      <span className="text-capitalize">
                        {payment.PaymentMethod === 'card' ? 'Credit Card' : payment.PaymentMethod}
                      </span>
                    </td>
                    <td style={{ maxWidth: '200px', wordBreak: 'break-all' }}>
                      <small className="font-monospace">{payment.TransactionID || '-'}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BillingHistoryModal;